import { useTranslation } from 'react-i18next'
import AdminActivityFeed from './AdminActivityFeed'
import AdminHealthCard from './AdminHealthCard'
import AdminModerationQueue from './AdminModerationQueue'
import AdminSkeleton from './AdminSkeleton'
import AdminSparklineStat from './AdminSparklineStat'
import type {
  AdminActivityEvent,
  AdminEarningsSummary,
  AdminHealth,
  AdminOverview,
  AdminSection,
  AdminTrends,
} from './adminTypes'

type Props = {
  token: string
  overview: AdminOverview | null
  earnings: AdminEarningsSummary | null
  trends: AdminTrends | null
  activity: AdminActivityEvent[]
  health: AdminHealth | null
  loading: boolean
  error: string
  lastUpdated: Date | null
  onRefresh: () => void
  onNavigate: (section: AdminSection, hint?: string) => void
  onOpenTransaction: (id: number) => void
  onOpenUser: (userId: number) => void
}

export default function AdminOverviewPanel({
  token,
  overview,
  earnings,
  trends,
  activity,
  health,
  loading,
  error,
  lastUpdated,
  onRefresh,
  onNavigate,
  onOpenTransaction,
  onOpenUser,
}: Props) {
  const { t, i18n } = useTranslation()

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.overview')}</h2>
          <p className="vc-admin-panel__hint">
            {lastUpdated
              ? t('admin.lastUpdated', {
                  time: lastUpdated.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
                })
              : t('admin.footnote')}
          </p>
        </div>
        <button type="button" className="vc-admin-btn vc-admin-btn--ghost" onClick={onRefresh}>
          {loading ? '…' : t('catalog.refresh')}
        </button>
      </header>

      {error ? <p className="vc-admin-error">{error}</p> : null}
      {loading && !overview ? <AdminSkeleton variant="stats" rows={8} /> : null}

      {overview ? (
        <AdminModerationQueue
          token={token}
          overview={overview}
          onNavigate={onNavigate}
          onOpenTransaction={onOpenTransaction}
          onOpenUser={onOpenUser}
        />
      ) : null}

      {overview && trends ? (
        <div className="vc-admin-stats">
          <AdminSparklineStat
            value={overview.users_total}
            label={t('admin.usersTotal')}
            series={trends.series.users}
            delta={trends.deltas.users_total}
            onClick={() => onNavigate('users')}
          />
          <AdminSparklineStat
            value={overview.listings_active}
            label={t('admin.listingsActive')}
            series={trends.series.listings}
            delta={trends.deltas.listings_active}
            onClick={() => onNavigate('listings')}
          />
          <AdminSparklineStat
            value={overview.offers_total}
            label={t('admin.offers')}
            onClick={() => onNavigate('transactions')}
          />
          <AdminSparklineStat
            value={overview.transactions_total}
            label={t('admin.transactions')}
            onClick={() => onNavigate('transactions')}
          />
          <AdminSparklineStat
            value={overview.phone_models_total}
            label={t('admin.phoneModels')}
            onClick={() => onNavigate('models')}
          />
          <AdminSparklineStat
            value={`€${overview.platform_fees_eur.toFixed(0)}`}
            label={t('admin.platformFees')}
            series={trends.series.revenue_eur}
            delta={trends.deltas.platform_fees_eur}
            onClick={() => onNavigate('earnings')}
          />
          <AdminSparklineStat
            value={overview.listings_above_ai_guidance}
            label={t('admin.aboveAiGuidance')}
            warn
            onClick={() => onNavigate('listings', 'above_ai')}
          />
          <AdminSparklineStat
            value={overview.pending_verifications}
            label={t('admin.pendingVerifications')}
            warn={overview.pending_verifications > 0}
            onClick={() => onNavigate('users')}
          />
        </div>
      ) : null}

      <section className="vc-admin-card vc-admin-card--compact">
        <h3>{t('admin.liveActivity')}</h3>
        <AdminActivityFeed items={activity} />
      </section>

      {earnings ? (
        <section className="vc-admin-card vc-admin-card--compact">
          <h3>{t('admin.earnings')}</h3>
          <div className="vc-admin-inline-stats">
            <span>
              {t('admin.platformFees')}: <strong>€{earnings.platform_fees_eur.toFixed(2)}</strong>
            </span>
            <span>
              {t('admin.sellerPayouts')}: <strong>€{earnings.seller_payouts_eur.toFixed(2)}</strong>
            </span>
            <span>
              {t('admin.completedDeals')}: <strong>{earnings.completed_transactions}</strong>
            </span>
          </div>
        </section>
      ) : null}

      <AdminHealthCard health={health} />
    </div>
  )
}
